import { useQuery } from "@tanstack/react-query";

import {
  IAdminAlert,
  IAdminAlertHistoryEntry,
} from "../../../@types/admin/AdminAlert";
import { alertsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/**
 * Configured alerts — the whole set, or one patient's when `patientId` is
 * given. Both forms back the shared `AlertsPanel`.
 */
export function useAdminAlerts(patientId?: number) {
  const { data, isLoading, isError } = useQuery<IAdminAlert[]>({
    queryKey: ["admin-alerts", patientId ?? "all"],
    queryFn: async () => ensureOk(await alertsService.getAlerts(patientId)),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { alerts: data ?? [], isLoading, isError };
}

/**
 * One alert's change history. Idle until an alert is chosen, matching
 * Retool's history modal, which only ran its query once opened.
 */
export function useAdminAlertHistory(alertId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IAdminAlertHistoryEntry[]>({
    queryKey: ["admin-alert-history", alertId],
    queryFn: async () =>
      ensureOk(await alertsService.getAlertHistory(alertId!)),
    enabled: alertId !== undefined,
    refetchOnMount: "always",
  });

  return { history: data ?? [], isLoading, isError };
}
